import { useEffect, useMemo, useState } from "react";
import AppShell from "../shell/AppShell.jsx";
import DetailDrawer from "../components/DetailDrawer.jsx";
import { useAgentPortalGuard } from "./useAgentPortalGuard.js";
import AgentPortalGuardState from "./AgentPortalGuardState.jsx";
import { useGeofenceZonesList, GEOFENCE_ZONES_DEFAULT_PAGE_SIZE } from "../geofence/useGeofenceZonesList.js";
import { getGeofenceZoneDetail, createGeofenceZone, updateGeofenceZone } from "../geofence/geofenceApi.js";
import "./agentZones.css";
import "../pages/states.css";
import Pagination from "../components/Pagination.jsx";

import PageSkeleton, { DetailSkeleton } from "../components/Skeleton.jsx";
const CRUMBS = ["Backoffice", "Agent", "Zones"];
const DRAWER_TITLE_ID = "az-drawer-title";
const SEARCH_DEBOUNCE_MS = 350;

const STATUS_BADGE_VARIANT = { Active: "success", Inactive: "neutral" };

function statusBadgeVariant(status) {
  return STATUS_BADGE_VARIANT[status] ?? "neutral";
}

function formatDateTh(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleDateString("th-TH-u-ca-gregory", { day: "2-digit", month: "short", year: "numeric" });
}

function describeListError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  return "กรุณาลองรีเฟรชหน้านี้อีกครั้ง หากยังพบปัญหากรุณาติดต่อฝ่ายปฏิบัติการ";
}

function describeWriteError(error) {
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ทำรายการนี้ เฉพาะ SuperAdmin หรือ Agent เจ้าของโซนเท่านั้น";
  }
  if (error?.status === 400) {
    return "กรุณาตรวจสอบข้อมูลที่กรอกอีกครั้ง";
  }
  return "ไม่สามารถบันทึกรายการนี้ได้ กรุณาลองใหม่อีกครั้ง";
}

const EMPTY_FORM = { zoneName: "", employerId: "", centerLatitude: "", centerLongitude: "", radiusMeters: "", note: "" };

/**
 * /backoffice/agent/zones - Prompt 138's Agent Portal Geofence Zones screen.
 * Reuses the real Prompt 118 Geofence Zone API (../geofence/geofenceApi.js +
 * useGeofenceZonesList.js) completely unchanged - no new backend endpoint.
 * Circle zones only, coordinates always shown as plain numbers - no map is
 * rendered anywhere in this codebase. Create/Edit work here because the
 * backend already treats the owning Agent as a real writer, not just
 * SuperAdmin (GeofenceZoneService.CreateZoneAsync auto-stamps a
 * non-SuperAdmin caller's own AgentId onto the new zone) - this page never
 * adds a client-side role gate on top of that, it only reflects the real
 * nextActions.canEdit the API itself returns per zone.
 */
export default function AgentZonesPage() {
  const guard = useAgentPortalGuard();

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState(null);
  const [drawerView, setDrawerView] = useState(null); // null | "detail" | "create" | "edit"
  const [detailState, setDetailState] = useState({ status: "idle", data: null, error: null, forId: null });
  const [detailReloadToken, setDetailReloadToken] = useState(0);
  const [createForm, setCreateForm] = useState(EMPTY_FORM);
  const [createSubmit, setCreateSubmit] = useState({ status: "idle", error: null });
  const [editForm, setEditForm] = useState(null);
  const [editSubmit, setEditSubmit] = useState({ status: "idle", error: null });

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  const filters = useMemo(() => ({ page, pageSize: GEOFENCE_ZONES_DEFAULT_PAGE_SIZE, search, zoneStatus: statusFilter }), [page, search, statusFilter]);
  const { status: listStatus, data: listData, error: listError, refetch } = useGeofenceZonesList(guard.status === "ready" ? filters : null);

  useEffect(() => {
    if (!selectedId) {
      setDetailState({ status: "idle", data: null, error: null, forId: null });
      return undefined;
    }
    let cancelled = false;
    setDetailState((previous) => ({ status: "loading", data: previous.forId === selectedId ? previous.data : null, error: null, forId: selectedId }));
    getGeofenceZoneDetail(selectedId)
      .then((data) => !cancelled && setDetailState({ status: "ready", data, error: null, forId: selectedId }))
      .catch((error) => !cancelled && setDetailState({ status: "error", data: null, error, forId: selectedId }));
    return () => {
      cancelled = true;
    };
  }, [selectedId, detailReloadToken]);

  if (guard.status !== "ready") {
    return <AgentPortalGuardState crumbs={CRUMBS} guard={guard} />;
  }

  function openDetail(id) {
    setSelectedId(id);
    setDrawerView("detail");
  }

  function openCreate() {
    setCreateForm(EMPTY_FORM);
    setCreateSubmit({ status: "idle", error: null });
    setDrawerView("create");
  }

  function openEdit() {
    const detail = detailState.data;
    if (!detail) {
      return;
    }
    setEditForm({
      zoneName: detail.zoneName,
      centerLatitude: String(detail.centerLatitude ?? ""),
      centerLongitude: String(detail.centerLongitude ?? ""),
      radiusMeters: String(detail.radiusMeters ?? ""),
      zoneStatus: detail.zoneStatus,
      note: detail.note ?? "",
    });
    setEditSubmit({ status: "idle", error: null });
    setDrawerView("edit");
  }

  function closeDrawer() {
    setDrawerView(null);
    setSelectedId(null);
  }

  function handleCreateSubmit() {
    if (!createForm.zoneName.trim()) {
      setCreateSubmit({ status: "error", error: { message: "กรุณาระบุ zoneName" } });
      return;
    }
    setCreateSubmit({ status: "submitting", error: null });
    createGeofenceZone(createForm)
      .then(() => {
        setCreateSubmit({ status: "success", error: null });
        refetch();
        closeDrawer();
      })
      .catch((error) => setCreateSubmit({ status: "error", error }));
  }

  function handleEditSubmit() {
    setEditSubmit({ status: "submitting", error: null });
    updateGeofenceZone(selectedId, editForm)
      .then(() => {
        setEditSubmit({ status: "success", error: null });
        setDetailReloadToken((token) => token + 1);
        refetch();
        setDrawerView("detail");
      })
      .catch((error) => setEditSubmit({ status: "error", error }));
  }

  const items = listData?.items ?? [];
  const totalCount = listData?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / GEOFENCE_ZONES_DEFAULT_PAGE_SIZE));

  return (
    <AppShell crumbs={CRUMBS}>
      <div className="wx-az-page">
        <header className="wx-az-head">
          <div>
            <h1>Zones ของสายงานฉัน</h1>
            <p>Geofence Zone แบบวงกลมในสายงานของคุณ พิกัดแสดงเป็นตัวเลขเท่านั้น ไม่มีแผนที่จริง</p>
          </div>
          <button type="button" className="wx-button wx-button--primary wx-az-create-button" onClick={openCreate}>
            + Create Zone
          </button>
        </header>

        <div className="wx-az-toolbar">
          <label className="wx-az-search">
            <span className="wx-visually-hidden">ค้นหาชื่อ Zone</span>
            <input type="search" placeholder="ค้นหาชื่อ Zone..." value={searchInput} onChange={(event) => setSearchInput(event.target.value)} />
          </label>
          <label className="wx-az-filter">
            <span>สถานะ</span>
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <option value="">ทั้งหมด</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </label>
        </div>

        <section className="wx-panel wx-az-list-panel">
          <div className="wx-az-list-head">
            <h2 className="wx-panel-title">รายการ Zone</h2>
            <span className="wx-az-list-count">{totalCount} zone</span>
          </div>

          {listStatus === "error" && listData === null ? (
            <div className="wx-state-card wx-state-card--danger" role="alert">
              <p className="wx-state-body">{describeListError(listError)}</p>
            </div>
          ) : listStatus === "loading" && listData === null ? (
            <PageSkeleton variant="map" label="กำลังโหลดรายการ Zone..." />
          ) : items.length === 0 ? (
            <p className="wx-empty-list">ยังไม่มี Zone ในสายงานของคุณ</p>
          ) : (
            <>
              <div className="wx-az-table-wrap">
                <table className="wx-az-table">
                  <thead>
                    <tr>
                      <th>Zone</th>
                      <th>รัศมี (m)</th>
                      <th>แรงงานที่กำหนด</th>
                      <th>สถานะ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.zoneId} className="wx-az-row" onClick={() => openDetail(item.zoneId)}>
                        <td>
                          <strong className="wx-az-cell-strong">{item.zoneName}</strong>
                          <span className="wx-az-code">{item.zoneCode}</span>
                        </td>
                        <td>{item.radiusMeters}</td>
                        <td>{item.assignedWorkerCount}</td>
                        <td>
                          <span className={`wx-badge wx-badge--${statusBadgeVariant(item.zoneStatus)}`}>{item.zoneStatus}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </>
          )}
        </section>
      </div>

      <DetailDrawer open={drawerView !== null} onClose={closeDrawer} labelledBy={DRAWER_TITLE_ID} selectableSelector="tr.wx-az-row, .wx-az-create-button">
        {drawerView === "detail" && <ZoneDetailContent titleId={DRAWER_TITLE_ID} state={detailState} onEdit={openEdit} />}
        {drawerView === "create" && (
          <ZoneCreateForm titleId={DRAWER_TITLE_ID} form={createForm} onChange={(patch) => setCreateForm((prev) => ({ ...prev, ...patch }))} submitState={createSubmit} onSubmit={handleCreateSubmit} onClose={closeDrawer} />
        )}
        {drawerView === "edit" && editForm && (
          <ZoneEditForm titleId={DRAWER_TITLE_ID} form={editForm} onChange={(patch) => setEditForm((prev) => ({ ...prev, ...patch }))} submitState={editSubmit} onSubmit={handleEditSubmit} onBack={() => setDrawerView("detail")} />
        )}
      </DetailDrawer>
    </AppShell>
  );
}

function ZoneDetailContent({ titleId, state, onEdit }) {
  if (state.status === "loading" || state.status === "idle") {
    return (
      <DetailSkeleton label="กำลังโหลดรายละเอียด..." />
    );
  }
  if (state.status === "error") {
    return (
      <div className="wx-az-drawer-state" role="alert">
        <h2 id={titleId} className="wx-state-title">
          ไม่สามารถโหลดรายละเอียดได้
        </h2>
      </div>
    );
  }

  const detail = state.data;

  return (
    <div className="wx-az-drawer">
      <span className="wx-az-drawer-kicker">ZONE · DETAIL</span>
      <h2 id={titleId} className="wx-az-drawer-title">
        {detail.zoneName}
      </h2>
      <p className="wx-az-drawer-sub">{detail.zoneCode}</p>
      <div className="wx-az-drawer-status">
        <span className={`wx-badge wx-badge--${statusBadgeVariant(detail.zoneStatus)}`}>{detail.zoneStatus}</span>
      </div>

      <section className="wx-az-dsec">
        <h3>ตำแหน่ง (ตัวเลขเท่านั้น ไม่มีแผนที่)</h3>
        <div className="wx-az-kv-row">
          <span>Latitude</span>
          <strong>{detail.centerLatitude}</strong>
        </div>
        <div className="wx-az-kv-row">
          <span>Longitude</span>
          <strong>{detail.centerLongitude}</strong>
        </div>
        <div className="wx-az-kv-row">
          <span>รัศมี</span>
          <strong>{detail.radiusMeters} m</strong>
        </div>
      </section>

      <section className="wx-az-dsec">
        <h3>สถานที่</h3>
        <div className="wx-az-kv-row">
          <span>นายจ้าง</span>
          <strong>{detail.employerName ?? "-"}</strong>
        </div>
        <div className="wx-az-kv-row">
        </div>
        <div className="wx-az-kv-row">
          <span>สร้างเมื่อ</span>
          <strong>{formatDateTh(detail.createdAtUtc) ?? "-"}</strong>
        </div>
      </section>

      <section className="wx-az-dsec">
        <h3>แรงงานที่กำหนด ({detail.assignedWorkers.length})</h3>
        {detail.assignedWorkers.length === 0 ? (
          <p className="wx-empty-list">ยังไม่มีแรงงานถูกกำหนดใน Zone นี้</p>
        ) : (
          detail.assignedWorkers.map((worker) => (
            <div className="wx-az-kv-row" key={worker.workerId}>
              <span>{worker.workerName ?? worker.workerCode}</span>
              <strong>{worker.assignmentStatus}</strong>
            </div>
          ))
        )}
      </section>

      {detail.nextActions.canEdit && (
        <div className="wx-az-drawer-actions">
          <button type="button" className="wx-button wx-button--primary wx-az-drawer-actions-full" onClick={onEdit}>
            แก้ไข Zone
          </button>
        </div>
      )}
    </div>
  );
}

function ZoneCreateForm({ titleId, form, onChange, submitState, onSubmit, onClose }) {
  const submitting = submitState.status === "submitting";
  return (
    <div className="wx-az-drawer">
      <span className="wx-az-drawer-kicker">CREATE ZONE</span>
      <h2 id={titleId} className="wx-az-drawer-title">
        สร้าง Zone ใหม่
      </h2>
      <p className="wx-az-drawer-sub">สร้างโซนใหม่สำหรับนายจ้างในสายงานของคุณ</p>

      <label className="wx-az-field">
        <span>
          ชื่อโซน <b>*</b>
        </span>
        <input type="text" aria-label="ชื่อโซน" value={form.zoneName} onChange={(event) => onChange({ zoneName: event.target.value })} />
      </label>
      <label className="wx-az-field">
        <span>รหัสนายจ้าง</span>
        <input type="text" aria-label="รหัสนายจ้าง" value={form.employerId} onChange={(event) => onChange({ employerId: event.target.value })} />
      </label>
      <label className="wx-az-field">
      </label>
      <div className="wx-az-form-row">
        <label className="wx-az-field">
          <span>ละติจูดจุดกึ่งกลาง</span>
          <input type="text" aria-label="ละติจูดจุดกึ่งกลาง" value={form.centerLatitude} onChange={(event) => onChange({ centerLatitude: event.target.value })} />
        </label>
        <label className="wx-az-field">
          <span>ลองจิจูดจุดกึ่งกลาง</span>
          <input type="text" aria-label="ลองจิจูดจุดกึ่งกลาง" value={form.centerLongitude} onChange={(event) => onChange({ centerLongitude: event.target.value })} />
        </label>
      </div>
      <label className="wx-az-field">
        <span>รัศมี (เมตร)</span>
        <input type="text" aria-label="รัศมี (เมตร)" value={form.radiusMeters} onChange={(event) => onChange({ radiusMeters: event.target.value })} />
      </label>
      <label className="wx-az-field">
        <span>note</span>
        <textarea aria-label="note" value={form.note} onChange={(event) => onChange({ note: event.target.value })} />
      </label>

      {submitState.status === "error" && (
        <p className="wx-az-form-message" role="alert">
          {describeWriteError(submitState.error)}
        </p>
      )}

      <div className="wx-az-drawer-actions">
        <button type="button" className="wx-button" onClick={onClose} disabled={submitting}>
          ยกเลิก
        </button>
        <button type="button" className="wx-button wx-button--primary" onClick={onSubmit} disabled={submitting}>
          {submitting ? "กำลังสร้าง..." : "สร้าง Zone"}
        </button>
      </div>
    </div>
  );
}

function ZoneEditForm({ titleId, form, onChange, submitState, onSubmit, onBack }) {
  const submitting = submitState.status === "submitting";
  return (
    <div className="wx-az-drawer">
      <span className="wx-az-drawer-kicker">EDIT ZONE</span>
      <h2 id={titleId} className="wx-az-drawer-title">
        แก้ไข Zone
      </h2>

      <label className="wx-az-field">
        <span>ชื่อโซน</span>
        <input type="text" aria-label="ชื่อโซน" value={form.zoneName} onChange={(event) => onChange({ zoneName: event.target.value })} />
      </label>
      <div className="wx-az-form-row">
        <label className="wx-az-field">
          <span>ละติจูดจุดกึ่งกลาง</span>
          <input type="text" aria-label="ละติจูดจุดกึ่งกลาง" value={form.centerLatitude} onChange={(event) => onChange({ centerLatitude: event.target.value })} />
        </label>
        <label className="wx-az-field">
          <span>ลองจิจูดจุดกึ่งกลาง</span>
          <input type="text" aria-label="ลองจิจูดจุดกึ่งกลาง" value={form.centerLongitude} onChange={(event) => onChange({ centerLongitude: event.target.value })} />
        </label>
      </div>
      <label className="wx-az-field">
        <span>รัศมี (เมตร)</span>
        <input type="text" aria-label="รัศมี (เมตร)" value={form.radiusMeters} onChange={(event) => onChange({ radiusMeters: event.target.value })} />
      </label>
      <label className="wx-az-field">
        <span>status</span>
        <select aria-label="status" value={form.zoneStatus} onChange={(event) => onChange({ zoneStatus: event.target.value })}>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
      </label>
      <label className="wx-az-field">
        <span>note</span>
        <textarea aria-label="note" value={form.note} onChange={(event) => onChange({ note: event.target.value })} />
      </label>

      {submitState.status === "error" && (
        <p className="wx-az-form-message" role="alert">
          {describeWriteError(submitState.error)}
        </p>
      )}

      <div className="wx-az-drawer-actions">
        <button type="button" className="wx-button wx-button--ghost" onClick={onBack} disabled={submitting}>
          ย้อนกลับ
        </button>
        <button type="button" className="wx-button wx-button--primary" onClick={onSubmit} disabled={submitting}>
          {submitting ? "กำลังบันทึก..." : "บันทึกการแก้ไข"}
        </button>
      </div>
    </div>
  );
}
